import { useState } from 'react';
import { supabase } from '../utils/supabaseClient';
import { useTrialPolicy } from '../utils/trialPolicy';
import { getUtm } from '../utils/utm';
import { trackEvent } from '../utils/track';
import { pendingReturn, emailRedirectFor } from '../utils/returnTo';
import './EmailGate.css';
import MatrixRain from './MatrixRain';

function isValidEmail(e) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());
}

const LANDING = 'https://web3kurzus.hu';

const FEATURES = [
  'Élő árak & hőtérkép',
  'RSI-szkenner és RSI-áttekintés',
  'Derivatívák & Funding',
  'Portfóliókövető',
  'Trade Tervező & Napló',
  'Makro Naptár',
];

// ── Magic-link registration / login form ───────────────────────────────────────

function FullForm() {
  const { trialDays } = useTrialPolicy();
  const [email,      setEmail]      = useState('');
  const [aszf,       setAszf]       = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [emailError, setEmailError] = useState('');
  const [aszfError,  setAszfError]  = useState('');
  const [submitted,  setSubmitted]  = useState(false);
  const fromDemo = Boolean(pendingReturn());

  /* A szövegek a web3kurzus.hu/login-éval egyeznek (landing LoginPage.jsx). */
  async function handleSubmit(e) {
    e.preventDefault();
    let ok = true;
    if (!email.trim()) { setEmailError('Add meg az email címed.'); ok = false; }
    else if (!isValidEmail(email)) { setEmailError('Ellenőrizd az email címet.'); ok = false; }
    else setEmailError('');
    if (!aszf) { setAszfError('Az ÁSZF elfogadása kötelező a regisztrációhoz.'); ok = false; }
    else setAszfError('');
    if (!ok) return;

    setLoading(true);
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        /* Bemutatóból: a visszatérési cél a visszahívási címben is (returnTo.js, 3b);
           minden más esetben a régi, változatlan cím. */
        emailRedirectTo: emailRedirectFor(window.location.origin),
        /* Ugyanazok a kulcsok, mint a landingen. A newsletter_consent-et az
           AuthCallback írja át a profilba (csak friss regisztrációnál). */
        data: {
          gdpr_consent: true,
          gdpr_consent_at: new Date().toISOString(),
          newsletter_consent: newsletter,
          /* E-mailek 1. kör: a marketing-hozzájárulás a szöveg verziójával, mint a
             landingen; az adatbázis (email_consent_on_signup) naplózza. */
          ...(newsletter ? { marketing_consent: 'reg-v1-2026-11' } : {}),
          ...(Object.keys(getUtm()).length ? { utm: getUtm() } : {}),
        },
      },
    });
    setLoading(false);

    if (error) {
      setEmailError('Hiba történt. Ellenőrizd az email címet és próbáld újra.');
      return;
    }

    localStorage.setItem('terminal_email', email.trim());
    /* A bemutatóból indult regisztráció mérése (a landing cta_click mintájára). */
    if (fromDemo) trackEvent('cta_click', { page: 'terminal_belepo', cta: 'magic_link', forras: 'gyakorlo_demo', ...getUtm() });
    setSubmitted(true);
  }

  // ── Inbox pending screen ─────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="eg-overlay">
        <MatrixRain />
        <div className="eg-modal">
          <div className="eg-top-bar" />
          <div className="eg-inbox-wrap">
            <div className="eg-inbox-icon">📧</div>
            <div className="eg-inbox-title">Nézd meg a postafiókod!</div>
            <div className="eg-inbox-body">Elküldtük a belépési linket a következő címre:</div>
            <div className="eg-inbox-email">{email.trim()}</div>
            <div className="eg-inbox-tips">
              <span>Ha nem látod a levelet, ellenőrizd a spam / levélszemét mappát is. A link 1 óráig érvényes, egyszer használható.</span>
            </div>
            <button
              className="eg-submit"
              style={{ marginTop: 4 }}
              onClick={() => { setSubmitted(false); setEmail(''); setAszf(false); setNewsletter(false); }}
            >
              Más emailt adok meg
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Registration / login form ────────────────────────────────────────────────
  return (
    <div className="eg-overlay">
      <MatrixRain />
      <div className="eg-modal">
        <div className="eg-top-bar" />

        <div className="eg-head">
          {/* Belépőknek és új regisztrálóknak is ez látszik; a próbát a fiókban indítja (Plusz 1. kör). */}
          <span className="eg-badge">Ingyenes regisztráció · a PRO-próbát te indítod</span>
          <div className="eg-title">⚡ KRIPTO TERMINÁL</div>
          <div className="eg-subtitle">
            Élő piaci adatok, szkenner és kereskedési eszközök egy helyen, magyarul.
          </div>
          <div className="eg-subtitle eg-subtitle--trial">
            A {trialDays} napos PRO-próbát te indítod, amikor készen állsz.
          </div>
          {fromDemo && (
            <div className="eg-subtitle">A belépő link után visszaviszünk a döntési gyakorlóhoz.</div>
          )}
          <div className="eg-author">Dr. Janó Dániel</div>
        </div>

        <div className="eg-features">
          {FEATURES.map(f => (
            <div key={f} className="eg-feature">
              <span className="eg-check">✓</span>
              <span>{f}</span>
            </div>
          ))}
        </div>

        <div className="eg-nocard">
          Bankkártya nem kell. A próbaidő után semmit nem vonunk le automatikusan.
        </div>

        <form className="eg-form" onSubmit={handleSubmit} noValidate>
          <div className="eg-field">
            <input
              type="email"
              className={`eg-input${emailError ? ' eg-input--error' : ''}`}
              placeholder="email@pelda.hu"
              value={email}
              onChange={e => { setEmail(e.target.value); setEmailError(''); }}
              disabled={loading}
              autoComplete="email"
            />
            {emailError && <span className="eg-error">{emailError}</span>}
          </div>

          <div className="eg-field">
            <label className={`eg-checkbox-label${aszfError ? ' eg-checkbox-label--error' : ''}`}>
              <input
                type="checkbox"
                className="eg-checkbox"
                checked={aszf}
                onChange={e => { setAszf(e.target.checked); setAszfError(''); }}
                disabled={loading}
              />
              <span className="eg-checkbox-text">
                Elolvastam és elfogadom az{' '}
                <a href={`${LANDING}/aszf`} target="_blank" rel="noopener noreferrer" className="eg-gdpr-link" onClick={e => e.stopPropagation()}>
                  ÁSZF-t
                </a>{' '}
                és az{' '}
                <a href={`${LANDING}/adatkezelesi-tajekoztato`} target="_blank" rel="noopener noreferrer" className="eg-gdpr-link" onClick={e => e.stopPropagation()}>
                  Adatkezelési Tájékoztatót
                </a>. Tudomásul veszem, hogy betöltöttem a 18. életévemet.
              </span>
            </label>
            {aszfError && <span className="eg-error">{aszfError}</span>}
          </div>

          <div className="eg-field">
            <label className="eg-checkbox-label">
              <input
                type="checkbox"
                className="eg-checkbox"
                checked={newsletter}
                onChange={e => setNewsletter(e.target.checked)}
                disabled={loading}
              />
              <span className="eg-checkbox-text">
                Kérem a web3kurzus tippjeit és ajánlatait e-mailben. Bármikor leiratkozhatok.
              </span>
            </label>
          </div>

          <div className="eg-privacy-notice">
            🔒 A portfóliód és a naplód csak az eszközödön van. Az e-mail-címedet és a használati statisztikát az Adatkezelési Tájékoztató szerint kezeljük.
          </div>

          <button type="submit" className="eg-submit" disabled={loading}>
            {loading
              ? <span className="eg-spinner">◈</span>
              : 'Belépés / regisztráció →'}
          </button>
          <div className="eg-submit-hint">Új vagy? A regisztráció ingyenes, bankkártya nélkül.</div>
          <div className="eg-submit-hint">Pár másodperc, és megjön a belépő linked. Jelszó nem kell.</div>
        </form>

        <div className="eg-security">
          Adataidat nem adjuk el, és nem adjuk át marketingcélra.
        </div>
      </div>
    </div>
  );
}

// ── Main export ────────────────────────────────────────────────────────────────

export default function EmailGate() {
  return <FullForm />;
}
